import _objectWithoutProperties from "/app/node_modules/.pnpm/next@13.5.3_@babel+core@7.14.3_@opentelemetry+api@1.4.1_react-dom@18.2.0_react@18.2.0_sass@1.49.9/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _defineProperty from "/app/node_modules/.pnpm/next@13.5.3_@babel+core@7.14.3_@opentelemetry+api@1.4.1_react-dom@18.2.0_react@18.2.0_sass@1.49.9/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
const _excluded = ["eventName", "userId"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { isEqual, throttle } from 'lodash';
import { BrazeCustomEvent, BRAZE_EVENTS_WITH_PROPERTIES, CONFIRMED_EMAIL_COOKIE_KEY, MARKED_AS_SOLD_COOKIE_KEY } from 'constants/braze';
import { getBrazeEventProperties } from 'data/api';
import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from 'libs/utils/localStorage';
import { deleteCookie, getCookie, saveCookie } from 'libs/utils/cookies';
import { logBrazeMessage } from './error-loggers';
import { flushBraze } from './async-utils';
export const DEFERRED_BRAZE_EVENTS_STORAGE_KEY = 'cached_braze_custom_events';
export function retrieveDeferredBrazeCustomEvents() {
  try {
    const currentEvents = getLocalStorageItem(DEFERRED_BRAZE_EVENTS_STORAGE_KEY);
    return currentEvents ? JSON.parse(currentEvents) : [];
  } catch (_unused) {
    // TODO: add error logging
    return [];
  }
}
export function deferBrazeCustomEvent(payload) {
  setLocalStorageItem(DEFERRED_BRAZE_EVENTS_STORAGE_KEY, JSON.stringify([...retrieveDeferredBrazeCustomEvents(), payload]));
}
/**
 * Removes *provided* Braze custom events that are stored in the local storage
 * @param {BrazeCustomEventPayload[]} eventPayloads - the events to remove from the local storage.
 */

export function removeDeferredBrazeCustomEvents(eventPayloads) {
  setLocalStorageItem(DEFERRED_BRAZE_EVENTS_STORAGE_KEY, JSON.stringify(retrieveDeferredBrazeCustomEvents().filter(deferredEvent => !eventPayloads.some(eventToRemove => isEqual(eventToRemove, deferredEvent)))));
}

const WAS_BRAZE_EVENT_LOGGED_OR_DEFERRED_STORAGE_KEY = (event, userExternalId) => "was_braze_".concat(event, "_logged_or_deferred_").concat(userExternalId);
/**
 * Removes *all* Braze custom events that are stored in the local storage
 */


export function removeAllDeferredBrazeCustomEvents() {
  removeLocalStorageItem(DEFERRED_BRAZE_EVENTS_STORAGE_KEY);
}

function wasLoggedOrDeferred(event, userExternalId) {
  try {
    return getLocalStorageItem(WAS_BRAZE_EVENT_LOGGED_OR_DEFERRED_STORAGE_KEY(event, userExternalId)) === 'true';
  } catch (_unused2) {
    return false;
  }
}

function markAsLoggedOrDeferred(event, userExternalId) {
  try {
    setLocalStorageItem(WAS_BRAZE_EVENT_LOGGED_OR_DEFERRED_STORAGE_KEY(event, userExternalId), 'true');
  } catch (_unused3) {// Do nothing
  }
}
/**
 * Flushes Braze custom events that are stored in local storage
 * @param {BrazeCustomEventPayload} payload - the event payload to log
 * @param {boolean} options.defer - if true, defers the event for later logging with `flushCustomEvents`
 * @param {boolean} options.once - if true, logs the event only once
 */


export async function brazeLogCustomEvent(payload, options) {
  const {
    event,
    userExternalId,
    screen,
    modelId
  } = payload;

  async function getEventProperties() {
    const baseEventProperties = screen ? {
      screen
    } : {};
    if (!BRAZE_EVENTS_WITH_PROPERTIES.includes(event)) return baseEventProperties;
    const response = await getBrazeEventProperties({
      modelId,
      eventName: event
    });
    if ('errors' in response) throw new Error('failed retrieving properties');
    return _objectSpread(_objectSpread({}, baseEventProperties), response.properties);
  }

  try {
    if (options !== null && options !== void 0 && options.oncePerUser && userExternalId) {
      if (wasLoggedOrDeferred(event, userExternalId)) return;
      markAsLoggedOrDeferred(event, userExternalId);
    }

    if (options !== null && options !== void 0 && options.defer) {
      deferBrazeCustomEvent(payload);
      return;
    }

    const {
      logCustomEvent
    } = await import('@braze/web-sdk'); // if Braze is not initialized before calling this function, then this will throw

    logCustomEvent(event, await getEventProperties());
  } catch (error) {
    // TODO: check error message and log an error if not related to initialization
    if (userExternalId) deferBrazeCustomEvent(payload);
    logBrazeMessage("eventLoggingError(event: ".concat(event, ", reason: ").concat(error.message, ")"), "userExternalId: ".concat(String(userExternalId)));
  }
}
export async function flushCustomEventsInLocalStorage(userExternalId) {
  const eventsToFlush = retrieveDeferredBrazeCustomEvents().filter(event => event.userExternalId === userExternalId);
  if (eventsToFlush.length === 0) return;
  const loggedEvents = (await Promise.allSettled(eventsToFlush.map(payload => brazeLogCustomEvent(payload)))).map((result, index) => result.status === 'fulfilled' ? eventsToFlush[index] : null).filter(event => event !== null);
  removeDeferredBrazeCustomEvents(loggedEvents);
}
const USER_ID_SEPARATOR = ',';
export const getBrazeEventsFromCookies = () => {
  const confirmedEmailEvents = getCookie(CONFIRMED_EMAIL_COOKIE_KEY); // TODO: [KK-1732] remove marked_as_sold cookie logic, as it is no longer used

  const markedAsSoldTransactionId = getCookie(MARKED_AS_SOLD_COOKIE_KEY);
  const result = [];

  if (confirmedEmailEvents) {
    const userIds = confirmedEmailEvents.split(USER_ID_SEPARATOR);
    const events = userIds.map(userId => ({
      eventName: BrazeCustomEvent.ConfirmedEmail,
      userId
    }));
    result.push(...events);
  }

  if (markedAsSoldTransactionId && !Number.isNaN(Number(markedAsSoldTransactionId))) result.push({
    eventName: BrazeCustomEvent.MarkedAsSold,
    modelId: Number(markedAsSoldTransactionId)
  });
  return result;
};
export const removeCustomEventsInCookies = userId => {
  const confirmedEmailEvents = getCookie(CONFIRMED_EMAIL_COOKIE_KEY);

  if (!userId) {
    deleteCookie(CONFIRMED_EMAIL_COOKIE_KEY);
    deleteCookie(MARKED_AS_SOLD_COOKIE_KEY);
    return;
  }

  if (confirmedEmailEvents) {
    const userIds = confirmedEmailEvents.split(USER_ID_SEPARATOR);
    const newCookieValue = userIds.filter(id => Number(id) !== userId).join(USER_ID_SEPARATOR);
    if (newCookieValue) saveCookie(CONFIRMED_EMAIL_COOKIE_KEY, newCookieValue);else deleteCookie(CONFIRMED_EMAIL_COOKIE_KEY);
  }

  deleteCookie(MARKED_AS_SOLD_COOKIE_KEY);
};
export async function flushCustomEventsInCookies(userId, userExternalId) {
  const eventsFromCookies = getBrazeEventsFromCookies().filter(event => Number(event.userId) === userId);
  if (eventsFromCookies.length === 0) return;
  await Promise.allSettled(eventsFromCookies.map(async event => {
    const {
      eventName,
      userId: _
    } = event,
          payload = _objectWithoutProperties(event, _excluded);

    await brazeLogCustomEvent(_objectSpread({
      event: eventName,
      userExternalId
    }, payload));
  }));
  removeCustomEventsInCookies(userId);
}
export const flushCustomEvents = (userExternalId, userId) => {
  Promise.allSettled([flushCustomEventsInLocalStorage(userExternalId), flushCustomEventsInCookies(userId, userExternalId)]).then(flushBraze);
};
const FLUSH_CUSTOM_EVENTS_MIN_INTERVAL = 7000;
export const flushCustomEventsThrottled = throttle(flushCustomEvents, FLUSH_CUSTOM_EVENTS_MIN_INTERVAL, {
  leading: true,
  trailing: true
});